import { AxiosResponse } from "axios";
import { BaseService } from "services/_baseService";
import appAxios from "services/axios";
import { Ticket, TicketCloseReason } from "interfaces/entities/ticket.model";
import {
    AntiRatingParams,
    AttentionQueueParams,
    AttentionQueueResponse,
    BranchesAntiRatingResponse,
    CloseReasonsStatsParams,
    CloseReasonsStatsResponse,
    ComplaintsStatsResponse,
    ResponseTimeTrendParams,
    ResponseTimeTrendResponse,
    SlaBreachesParams,
    SlaBreachesResponse,
    TicketsTopicsParams,
    TicketsTopicsStatsResponse,
    TripsAntiRatingResponse,
} from "interfaces/entities/ticketStats";

class TicketsAdminService extends BaseService<Ticket> {
    constructor() {
        super(`tickets`);
    }

    // Перевести тикет в RESOLVED. Сервер сам ставит resolvedAt и валидирует переход.
    resolve = (ticketID: number | string): Promise<AxiosResponse<Ticket>> => {
        return appAxios.post(`${this.pathWithOrg()}/${ticketID}/resolve`);
    };

    // Закрыть тикет. reason обязателен. Доступные причины см. MANUAL_CLOSE_REASONS.
    close = (
        ticketID: number | string,
        reason: TicketCloseReason
    ): Promise<AxiosResponse<Ticket>> => {
        return appAxios.post(`${this.pathWithOrg()}/${ticketID}/close`, { reason });
    };

    // Переоткрыть закрытый тикет (возвращает в IN_PROGRESS).
    reopen = (ticketID: number | string): Promise<AxiosResponse<Ticket>> => {
        return appAxios.post(`${this.pathWithOrg()}/${ticketID}/reopen`);
    };

    // Список ответов по тикету
    getMessages = (
        ticketID: number | string,
        queryParams: object = {}
    ): Promise<AxiosResponse<{ rows: any[]; count: number }>> => {
        return appAxios.get(`${this.pathWithOrg()}/${ticketID}/messages`, { params: queryParams });
    };

    // Отправка ответа сотрудника по тикету
    sendMessage = (
        ticketID: number | string,
        payload: { content: string }
    ): Promise<AxiosResponse<any>> => {
        return appAxios.post(`${this.pathWithOrg()}/${ticketID}/messages`, payload);
    };

    // Улучшение черновика ответа через ИИ (перефразирование + грамматика)
    rewriteMessage = (
        ticketID: number | string,
        payload: { content: string }
    ): Promise<AxiosResponse<{ rephrased: string }>> => {
        return appAxios.post(`${this.pathWithOrg()}/${ticketID}/messages/rewrite`, payload);
    };

    // Перевод черновика ответа сотрудника между RU, KK и EN.
    // Если targetLanguage не указан — авто-направление RU↔KK (английский выбирать явно).
    translateMessage = (
        ticketID: number | string,
        payload: { content: string; targetLanguage?: 'ru' | 'kk' | 'en' }
    ): Promise<AxiosResponse<{ original: string; translated: string; targetLanguage: 'ru' | 'kk' | 'en'; model: string }>> => {
        return appAxios.post(`${this.pathWithOrg()}/${ticketID}/messages/translate`, payload);
    };

    // Расшифровка аудио-ответа сотрудника в текст (русский / казахский).
    // Возвращает текст. Сам файл на сервере не сохраняется.
    transcribeAudioMessage = (
        ticketID: number | string,
        audio: Blob | File,
        opts?: { language?: string; fileName?: string }
    ): Promise<AxiosResponse<{ text: string; language: string; model: string; sizeBytes: number }>> => {
        const formData = new FormData();
        const fileName = opts?.fileName || (audio instanceof File ? audio.name : 'voice.webm');
        formData.append('file', audio, fileName);
        if (opts?.language) formData.append('language', opts.language);
        return appAxios.post(`${this.pathWithOrg()}/${ticketID}/messages/transcribe-audio`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    };

    // Статистика жалоб для графика «Количество жалоб»
    getComplaintsStats = (
        year?: number,
        branchID?: number | string
    ): Promise<AxiosResponse<ComplaintsStatsResponse>> => {
        const params: any = {};
        if (year) params.year = year;
        if (branchID) params.branchID = branchID;
        return appAxios.get(`${this.pathWithOrg()}/stats/complaints`, {
            params: Object.keys(params).length ? params : undefined,
        });
    };

    // Статистика обращений по тематикам (категориям) для PieChart
    getTicketsTopicsStats = (
        params: TicketsTopicsParams & { branchID?: number | string } = {}
    ): Promise<AxiosResponse<TicketsTopicsStatsResponse>> => {
        return appAxios.get(`${this.pathWithOrg()}/stats/topics`, { params });
    };

    // Антирейтинг филиалов по количеству обращений (по умолчанию — жалоб)
    getBranchesAntiRating = (
        params: AntiRatingParams & { branchID?: number | string } = {}
    ): Promise<AxiosResponse<BranchesAntiRatingResponse>> => {
        return appAxios.get(`${this.pathWithOrg()}/stats/branches-anti-rating`, { params });
    };

    // Антирейтинг поездок (групповых поездок) по количеству обращений
    getTripsAntiRating = (
        params: AntiRatingParams & { branchID?: number | string } = {}
    ): Promise<AxiosResponse<TripsAntiRatingResponse>> => {
        return appAxios.get(`${this.pathWithOrg()}/stats/trips-anti-rating`, { params });
    };

    // Виджет «Очередь требующая внимания» (Dashboard)
    getAttentionQueue = (
        params: AttentionQueueParams = {}
    ): Promise<AxiosResponse<AttentionQueueResponse>> => {
        return appAxios.get(`${this.pathWithOrg()}/stats/attention-queue`, { params });
    };

    // Виджет «SLA-нарушители» (Dashboard)
    getSlaBreaches = (
        params: SlaBreachesParams = {}
    ): Promise<AxiosResponse<SlaBreachesResponse>> => {
        return appAxios.get(`${this.pathWithOrg()}/stats/sla-breaches`, { params });
    };

    // Тренд среднего времени решения и первого ответа за N дней
    getResponseTimeTrend = (
        params: ResponseTimeTrendParams & { branchID?: number | string } = {}
    ): Promise<AxiosResponse<ResponseTimeTrendResponse>> => {
        return appAxios.get(`${this.pathWithOrg()}/stats/response-time-trend`, { params });
    };

    // Структура закрытий: распределение closeReason за окно
    getCloseReasonsStats = (
        params: CloseReasonsStatsParams & { branchID?: number | string } = {}
    ): Promise<AxiosResponse<CloseReasonsStatsResponse>> => {
        return appAxios.get(`${this.pathWithOrg()}/stats/close-reasons`, { params });
    };

    // Экспорт сводного отчёта по обращениям в xlsx (с погодовым сравнением)
    exportXlsx = (
        params: Record<string, string | number> = {}
    ): Promise<AxiosResponse<Blob>> => {
        return appAxios.get(`${this.pathWithOrg()}/export-xlsx`, {
            params,
            responseType: 'blob',
        });
    };
}

export default new TicketsAdminService()
